import React from 'react';
// eslint-disable-next-line no-unused-vars -- `motion` is used as JSX (<motion.div>); the repo's eslint config lacks eslint-plugin-react so it can't detect JSX usage.
import { motion } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import SpecialtyIcon from '../common/SpecialtyIcon';
import WhoLabel from '../common/WhoLabel';
import CommonLoader from '../CommonLoader';
import { currencySymbol, doctorCurrencySymbol } from '../../utils/currency';
import { formatTime } from '../../utils/clock';

// Per-row translated specialty label. useMasterLabel is a hook, so it must be
// called once per row inside a dedicated child component (never inside .map).
const SpecialtyName = ({ specialtyId, specialtyName }) => {
  const label = useMasterLabel('masterSpecialty', specialtyId, 'name', specialtyName || '');
  return <span className="capitalize">{label || '—'}</span>;
};

// Format an ISO/date string into "Mon, Jun 2 · 09:00".
const formatDateTime = (apptDate) => {
  if (!apptDate) return '—';
  const d = new Date(apptDate);
  if (Number.isNaN(d.getTime())) return '—';
  const datePart = d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return `${datePart} · ${formatTime(d)}`; // "2:30 PM" — the one clock format
};

const UpcomingAppointmentsTable = ({
  dateRange,
  selectedPeriod,
  specialtyId,
  addressTypeName,
  upcomingFlat,
  loading,
  page,
  onPageChange,
}) => {
  const { t } = useTranslation('dashboard');

  // `selectedPeriod` is part of the shared dashboard prop contract but this table
  // keys solely off the resolved dateRange; reference it so it stays in the API.
  void selectedPeriod;
  // `dateRange`, `specialtyId`, and `addressTypeName` remain part of the shared
  // dashboard prop contract; the parent now uses them to drive the socket fetch.
  void dateRange;
  void specialtyId;
  void addressTypeName;

  const { selectedDoctor } = useSelector((state) => state.doctor);
  // The doctor's currency, from their country's currency code (utils/currency.js).
  const getCurrencySymbol = () => doctorCurrencySymbol(selectedDoctor);

  const rows = upcomingFlat?.data || [];
  const totalPages = upcomingFlat?.totalPages || 0;
  const totalItems = upcomingFlat?.totalItems || 0;
  const currentPage = upcomingFlat?.page || page;

  const formatPrice = (row) => {
    // The row's own currency when it carries one (code first), else the doctor's.
    const symbol = currencySymbol({ currencyCode: row?.currencyCode, currencySymbol: row?.currencySymbol })
      || getCurrencySymbol();
    return `${symbol}${Number(row?.price || 0).toLocaleString()}`;
  };

  // The server fixes the page size at 8 items.
  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * 8 + 1;
  const rangeEnd = Math.min(currentPage * 8, totalItems);

  return (
    <motion.div
      data-testid="upcoming-appointments-table"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl p-4 border border-gray-200/50 shadow-md"
    >
      <div data-testid="upcoming-appointments-header" className="flex items-center justify-between mb-4">
        <h3 data-testid="upcoming-appointments-title" className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
          <Calendar data-testid="upcoming-appointments-icon" className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
          {t('dashboard.upcoming_appointments', 'Upcoming appointments')}
        </h3>
      </div>

      {loading ? (
        <div data-testid="upcoming-appointments-loading" className="flex flex-col items-center justify-center py-12">
          <CommonLoader />
          <span data-testid="upcoming-appointments-loading-text" className="text-sm text-gray-600 mt-2">
            {t('dashboard.loading_appointments', 'Loading appointments...')}
          </span>
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="upcoming-appointments-empty" className="flex flex-col items-center justify-center py-12">
          <Calendar data-testid="upcoming-appointments-empty-icon" className="w-12 h-12 text-gray-300 mb-2" />
          <p data-testid="upcoming-appointments-empty-text" className="text-sm text-gray-600">
            {t('dashboard.no_upcoming_appointments', 'No upcoming appointments')}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop / tablet table (sm+) */}
          <div data-testid="upcoming-appointments-desktop" className="hidden sm:block">
            <div
              data-testid="upcoming-appointments-head-row"
              className="hidden sm:grid grid-cols-12 gap-2 px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              <div className="col-span-3">{t('dashboard.date_time', 'Date & time')}</div>
              <div className="col-span-3">{t('dashboard.patient', 'Patient')}</div>
              <div className="col-span-3">{t('dashboard.specialty', 'Specialty')}</div>
              <div className="col-span-2">{t('dashboard.type', 'Type')}</div>
              <div className="col-span-1 text-right">{t('dashboard.price', 'Price')}</div>
            </div>

            <div data-testid="upcoming-appointments-body" className="divide-y divide-gray-200">
              {rows.map((row, index) => (
                <div
                  key={row?.id ?? index}
                  data-testid={`upcoming-appointments-row-${row?.id ?? index}`}
                  className="grid grid-cols-12 gap-2 items-center px-2 py-3 text-sm"
                >
                  <div className="col-span-3 text-gray-700 font-medium">{formatDateTime(row?.apptDate)}</div>
                  <div className="col-span-3 text-gray-900 font-semibold truncate" title={row?.patientName || ''}>
                    {row?.patientName || '—'}
                  </div>
                  <div className="col-span-3">
                    <span
                      data-testid={`upcoming-appointments-specialty-pill-${row?.id ?? index}`}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium max-w-full"
                      style={{ backgroundColor: (row?.colour || '#4ABDBD') + '22', color: row?.colour || '#4ABDBD' }}
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: row?.colour || '#4ABDBD' }}
                      ></span>
                      <SpecialtyIcon specialtyId={row?.specialtyId} size={12} />
                      <span className="truncate">
                        <SpecialtyName specialtyId={row?.specialtyId} specialtyName={row?.specialtyName} />
                      </span>
                    </span>
                  </div>
                  <div
  className="col-span-2 truncate"
  style={{ color: row?.colour || '#4ABDBD' }}
>
  {row?.appointmentType || '—'}
</div>
                  <div className="col-span-1 text-right text-gray-900 font-semibold whitespace-nowrap">{formatPrice(row)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile stacked cards (<sm) */}
          <div data-testid="upcoming-appointments-mobile" className="block sm:hidden space-y-3">
            {rows.map((row, index) => (
              <div
                key={row?.id ?? index}
                data-testid={`upcoming-appointments-card-${row?.id ?? index}`}
                className="rounded-lg border border-gray-200/70 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 truncate">
                      {row?.patientName
                        ? <WhoLabel role="patient" name={row.patientName} nameClassName="font-semibold text-gray-900" testId={`upcoming-appointments-card-patient-${row?.id ?? index}`} />
                        : '—'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{formatDateTime(row?.apptDate)}</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatPrice(row)}</p>
                </div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <span
                    data-testid={`upcoming-appointments-mobile-pill-${row?.id ?? index}`}
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium max-w-[60%]"
                    style={{ backgroundColor: (row?.colour || '#4ABDBD') + '22', color: row?.colour || '#4ABDBD' }}
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: row?.colour || '#4ABDBD' }}
                    ></span>
                    <SpecialtyIcon specialtyId={row?.specialtyId} size={12} />
                    <span className="truncate">
                      <SpecialtyName specialtyId={row?.specialtyId} specialtyName={row?.specialtyName} />
                    </span>
                  </span>
                  <span className="text-xs text-gray-600 truncate">{row?.appointmentType || '—'}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              data-testid="upcoming-appointments-pagination"
              className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-200"
            >
              <p data-testid="upcoming-appointments-range" className="text-xs sm:text-sm text-gray-600">
                {t('dashboard.showing', 'Showing')} {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} {t('dashboard.of', 'of')}{' '}
                {totalItems.toLocaleString()}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="upcoming-appointments-prev"
                  onClick={() => onPageChange(page - 1)}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label={t('dashboard.previous', 'Previous')}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span data-testid="upcoming-appointments-page-indicator" className="text-xs sm:text-sm text-gray-600 px-1">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  data-testid="upcoming-appointments-next"
                  onClick={() => onPageChange(page + 1)}
                  disabled={currentPage >= totalPages}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label={t('dashboard.next', 'Next')}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default UpcomingAppointmentsTable;
